import axios from "axios";
import { ref, computed, watch } from "vue";
import { defineStore } from "pinia";
import { useRouter, useRoute } from "vue-router";
import { useModals } from "@/composables/useModals";
import { useGameHistory } from "@/composables/useGameHistory";
import { useFieldsStore } from "@/stores/storeFieldData";
import { useSettingsStore } from "@/stores/storeSettings";
import { useQuizMatchesStore } from "@/stores/storeQuizMatches";
import { sendMetrik, pushEvent, smartPixel, trackAdriver } from "@/utils/metrika";
import { sendAdriver } from "@/utils/metrika";
import { phoneFormatter } from "@/utils/formatter";
import { API_URL } from "@/utils/constans";
import { TEA_PREDICTION_TOKEN_KEY } from "@/data/teaPredictionQuestions";
import {
	clearPendingPromoCode,
	getPendingPromoCode,
	getPromoCodeErrorMessage,
} from "@/utils/promoCode";

export const useRequestsStore = defineStore("request", () => {
	const router = useRouter();
	const route = useRoute();
	const storeField = useFieldsStore();
	const storeSettings = useSettingsStore();
	const storeQuizMatches = useQuizMatchesStore();
	const { responseOk, closeModal, openModal } = useModals();
	const { getToken, clearGameData } = useGameHistory();

	const isClient = typeof window !== "undefined";

	const getTestToken = () => (isClient ? localStorage.getItem(TEA_PREDICTION_TOKEN_KEY) || "" : "");

	const clearTestToken = () => {
		if (isClient) {
			localStorage.removeItem(TEA_PREDICTION_TOKEN_KEY);
		}
	};

	const token = ref(null);
	const tokenGameWords = ref(null);
	const tokenGameEmoji = ref(null);
	if (isClient) {
		token.value = localStorage.getItem("token");
	}

	const setToken = (newToken) => {
		token.value = newToken;
		localStorage.setItem("token", newToken);
	};

	const removeToken = () => {
		token.value = null;
		localStorage.removeItem("token");
	};

	const loaderBtn = ref(false);
	const userData = ref({});
	const userReceiptsData = ref({});
	const userReceiptReults = ref([]);
	const userPrizesData = ref({});
	const listUserPrizes = ref([]);
	const listAllPrizes = ref([]);
	const winnersList = ref([]);
	const pointsList = ref([]);
	const responseError = ref("");
	const isRegistrationSuccess = ref(false);
	const isAuthenticated = computed(() => !!token.value);
	const isApprovedChecksCount = computed(() => userData.value?.approved_checks_count >= 1);
	const isMatchGamePrize = computed(() => listUserPrizes.value?.some((item) => item.prize.type === "match_3_game"));	
	const strUserLastFirsttName = computed(() => `${userData.value?.last_name || ''} ${userData.value?.first_name || ''}`);

	axios.interceptors.response.use(
		(response) => response,
		(error) => {
			if (error.response && error.response.status === 401) {
				removeToken();
				delete axios.defaults.headers.common["Authorization"];
			}
			return Promise.reject(error);
		},
	);

	const setAuthHeader = () => {
		const urlParams = new URLSearchParams(window.location.search);
		const tokenURL = urlParams.get("token");
		if (tokenURL) {
			token.value = tokenURL;
		} else {
			token.value = localStorage.getItem("token");
		}
		if (token.value) {
			axios.defaults.headers.common["Authorization"] = `Bearer ${token.value}`;
		} else {
			delete axios.defaults.headers.common["Authorization"];
		}
	};

	const registration = async () => {
		loaderBtn.value = true;
		const registrationParams = localStorage.getItem("registration_params") || "";
		const referenceCode = localStorage.getItem("ref") || "";

		try {
			const formData = new FormData();

			formData.append("first_name", storeField.fields.firstName.title);
			formData.append("last_name", storeField.fields.lastName.title);
			formData.append("phone_number", storeField.fields.phone.title);
			formData.append("email", storeField.fields.email.title);
			formData.append("registration_params", registrationParams);
			formData.append("referral_code", referenceCode);
			formData.append("is_policy", storeField.fields.rules.title);
			formData.append("is_rules", storeField.fields.rules.title);
			formData.append("agree_to_advertising", storeField.fields.agreeToAdvertising.title);

			const testToken = getTestToken();
			if (testToken) {
				formData.append("test_token", testToken);
			}

			const res = await axios.post(`${API_URL}/api/users/registration`, formData, {
				headers: {
					"Content-Type": "multipart/form-data",
				},
			});
			isRegistrationSuccess.value = true;
			sendMetrik("reachGoal", "signup_success");
			pushEvent("registr-success'")
			smartPixel('how')
			trackAdriver('sign-up')
			storeField.updateRegestrationBtn();
			localStorage.removeItem("registration_params");
			localStorage.removeItem("ref");

			if (res.data?.access) {
				setToken(res.data.access);
				setAuthHeader();
				await userInfo();
			}

			const promoHandled = await activatePendingPromoCodeIfExists();

			if (!promoHandled) {
				openModal("registrationEnd");
			}
		} catch (error) {
			console.error("Error sendRegistration", error);
			responseError.value = error.response?.data?.detail || "Ошибка при отправке данных.";
			console.log("error.response:", error.response);
		} finally {
			loaderBtn.value = false;
		}
	};

	const authorization = async () => {
		loaderBtn.value = true;
		const parameters = {
			email: storeField.fields.email.title,
			password: storeField.fields.password.title,
			game_token: getToken(),
		};

		const testToken = getTestToken();
		if (testToken) {
			parameters.test_token = testToken;
		}

		try {
			const res = await axios.post(`${API_URL}/api/users/authorization`, parameters);
			setToken(res.data.access);
			setAuthHeader();
			await userInfo();
			await storeQuizMatches.getGameMatch3();
			clearGameData();
			clearTestToken();

			closeModal();
			storeSettings.closeMobileMenu();
			pushEvent("login-success")
			smartPixel('enter_success');
			trackAdriver('log-in')
			storeField.updateFields("email", "disabledBtn", false);
			storeField.updateFields("password", "disabledBtn", false);
			await activatePendingPromoCodeIfExists();
			router.push({ name: "profile" });
		} catch (error) {
			console.log("error sendAuthorization:", error);
			responseError.value = error.response?.data?.detail || "Ошибка при отправке данных.";
			if (error.response.status === 557) {
				openModal("notVerified");
			}
		} finally {
			loaderBtn.value = false;
		}
	};

	const userInfo = async () => {
		tokenGameWords.value = localStorage.getItem("tokenGameFillword");
		tokenGameEmoji.value = localStorage.getItem("tokenGameEmoji");
		try {
			setAuthHeader();
			const res = await axios.get(`${API_URL}/api/users/me`);
			userData.value = res.data;
			initWidget();
			await userPrizes();
			await getPoints();
			// if (!!tokenGameWords.value) {
			// 	await userAddGameFillWords(tokenGameWords.value);
			// 	localStorage.removeItem("tokenGameFillword");
			// 	tokenGameWords.value = null;
			// }
			// if (!!tokenGameEmoji.value) {
			// 	await userAddGameEmoji(tokenGameEmoji.value);
			// 	localStorage.removeItem("tokenGameEmoji");
			// 	tokenGameEmoji.value = null;
			// }
		} catch (error) {
			if (error.response && error.response.status === 401) {
				console.warn("Пользователь не авторизован");
				userData.value = null;
			} else {
				console.error("Error getUserInfo", error);
				throw error;
			}
		}
	};

	const verification = async (token, uuid) => {
		try {
			const params = {
				token: token,
				uuid: uuid,
			};
			const res = await axios.patch(`${API_URL}/api/users/registration/accept`, null, { params });
			setToken(res.data.access);
			await userInfo();
			await activatePendingPromoCodeIfExists();
			router.push({ name: "profile" });
			// sendMetrik('reachGoal', 'SignIn_SentForm_Success')
		} catch (error) {
			console.error("Error verification", error);
			// responseError.value = error.response?.data?.detail || "Ошибка при отправке данных.";
			openModal("errorVerified");
		} finally {
		}
	};

	const repeatVerification = async () => {
		try {
			const parameters = {
				email: storeField.fields.email.title,
			};
			const res = await axios.post(`${API_URL}/api/users/resend-verification-email`, parameters);
			openModal("endRepeatVerified");
		} catch (error) {
			console.error("Error repeatVerification", error);
			responseError.value = error.response?.data?.detail || "Ошибка при отправке данных.";
		} finally {
		}
	};

	const recoveryPassword = async () => {
		loaderBtn.value = true;
		const parameters = {
			email: storeField.fields.email.title,
		};
		try {
			const res = await axios.post(`${API_URL}/api/users/recovery-password`, parameters);
			openModal("sentNewPassword");
		} catch (error) {
			console.log("error sendRecoveryPassword:", error);
			responseError.value = error.response?.data?.detail || "Ошибка при отправке данных.";
		} finally {
			loaderBtn.value = false;
		}
	};

	const changeUserProfile = async () => {
		const parameters = {
			fio: storeField.fields.fio.title,
			email: storeField.fields.email.title || userData.value.email,
			phone_number: storeField.fields.phone.title || userData.value.phone_number,
			city: storeField.fields.city.title,
			street: storeField.fields.street.title,
			house: storeField.fields.house.title,
			zip_code: storeField.fields.zipCode.title,
			apartment: storeField.fields.apartment.title,
		};
		loaderBtn.value = true;
		try {
			setAuthHeader();
			const res = await axios.post(`${API_URL}/api/users/address`, parameters);
			await userInfo();
			openModal("sentChangeUserProfile");
			setTimeout(() => {
				closeModal("sentChangeUserProfile");
			}, 2000);
		} catch (error) {
			console.log("error changeUserProfile", error);
			responseError.value = error.response?.data?.detail || "Ошибка при отправке данных.";
		} finally {
			loaderBtn.value = false;
		}
	};

	const changeUserPassword = async () => {
		loaderBtn.value = true;
		const new_password = { new_password: storeField.fields.passwordRepeat.title };
		try {
			setAuthHeader();
			await axios.patch(`${API_URL}/api/users/change-password`, new_password);
			openModal("sentChangeNewPassword");
			setTimeout(() => {
				closeModal("sentChangeNewPassword");
			}, 2000);
		} catch (error) {
			console.error("Error changePassword", error);
			responseError.value = error.response?.data?.detail || "Ошибка при отправке данных";
		} finally {
			loaderBtn.value = false;
		}
	};
	
	const getPrizes = async (type, availableOnly) => {
		loaderBtn.value = true;
		try {
			const params = { type }
			if (availableOnly !== undefined && availableOnly !== null) {
				params.available_only = availableOnly
			}
			let url = `${API_URL}/api/quiz/prizes?type`;
			let allResults = [];

			while (url) {
				const { data } = await axios.get(url, { params });
				allResults = [...allResults, ...data.results];
				url = data.next;
			}
			listAllPrizes.value = allResults;
			return allResults;
		} catch (error) {
			console.log("getPrizes:", error);
		} finally {
			loaderBtn.value = false;
		}
	};

	const getPoints = async () => {
		try {
			setAuthHeader();
			let url = `${API_URL}/api/points`;
			let allResults = [];

			while (url) {
				const { data } = await axios.get(url);
				allResults = [...allResults, ...data.results];
				url = data.next;
			}
			pointsList.value = allResults;
			return allResults;
		} catch (error) {
			console.log("getPoints:", error);
		}
	};

	const activatePromoCode = async (code) => {
		setAuthHeader();
		const trimmedCode = code.trim();

		const { data } = await axios.post(`${API_URL}/api/promocodes/activate/`, {
			code: trimmedCode,
		});

		if (userData.value) {
			userData.value.points_balance = data.balance;
		}

		await getPoints();

		return data;
	};

	const activatePendingPromoCodeIfExists = async () => {
		const code = getPendingPromoCode();

		if (!code || !token.value) {
			return false;
		}

		try {
			const data = await activatePromoCode(code);
			clearPendingPromoCode();
			openModal("promoCodeSuccess", {
				content: { points: data.points_awarded },
				size: "w400",
			});
			return true;
		} catch (error) {
			clearPendingPromoCode();
			openModal("promoCodeError", {
				content: { message: getPromoCodeErrorMessage(error) },
				size: "w400",
			});
			return true;
		}
	};

	const userPrizes = async () => {
		try {
			setAuthHeader();
			let url = `${API_URL}/api/quiz/user/prizes`;
			let allResults = [];
			while(url) {
				const { data } = await axios.get(url)
				allResults = [...allResults, ...data.results];
				url = data.next;
			}
			listUserPrizes.value = allResults;
			return allResults;
		} catch (error) {
			console.error("Error fetching prizes:", error);
		}
	};

	const downloadCertificate = async (id) => {
		loaderBtn.value = true;
		try {
			setAuthHeader();
			const { data } = await axios.get(`${API_URL}/api/quiz/user/prizes/additional-data/${id}`, { responseType: 'blob' });
			
			return data;
		} catch(error) {
			console.log('error downloadCertificate:', error);
		} finally {
			loaderBtn.value = false;
		}
	}

	const buyPrize = async (prizeId, responseModalId=null) => {
		
		loaderBtn.value = true;
		try {
			setAuthHeader();
			await axios.post(`${API_URL}/api/quiz/prizes/buy`, { prize: prizeId });
			if (!!responseModalId) {
				openModal(responseModalId);
			} else {
				openModal("successSetPrize");
			}
			await userInfo();
		} catch (error) {
			console.log(" buyPrize:", error);
			responseError.value = error.response?.data?.detail || "Ошибка при отправке данных";
		} finally {
			loaderBtn.value = false;
		}
	};

	const offsetReceipt = ref(0);
	let limitReceipt = 4;
	const loading = ref(false);
	const allLoaded = ref(false);
	const nextList = ref(null);

	// if(isMobile.value) {
	// 	limitReceipt = 4;
	// } else {
	// 	limitReceipt = 6;
	// }

	const mergeReceipts = (current, incoming) => {
		const map = new Map();
		[...incoming, ...current].forEach((r) => {
			map.set(r.id, r);
		});
		// возвращаем массив, отсортированный по дате (новые сверху)
		return Array.from(map.values()).sort((a, b) => new Date(b.upload_date) - new Date(a.upload_date));
	};

	const putRecieptInListReceipts = async () => {
		try {
			const res = await axios.get(`${API_URL}/api/quiz/user/receipts?limit=1000`);
			const results = res.data?.results || [];
			userReceiptsData.value = res.data;
			userReceiptReults.value = mergeReceipts(userReceiptReults.value, results);
		} catch (error) {
			console.log("putRecieptInListReceipts error:", error);
		}
	};

	// чек приходит на бэкенд не сразу после отправки в apmcheck:
	// обновляем список, пока не появится новый чек, но не дольше RECEIPT_POLL_ATTEMPTS попыток
	const RECEIPT_POLL_INTERVAL = 5000;
	const RECEIPT_POLL_ATTEMPTS = 24;

	const refreshAfterReceiptSent = async () => {
		// в списке может быть загружена только первая страница, поэтому новые чеки ищем относительно полного списка
		await putRecieptInListReceipts();
		const knownIds = new Set(userReceiptReults.value.map((receipt) => receipt.id));

		for (let attempt = 0; attempt < RECEIPT_POLL_ATTEMPTS; attempt++) {
			await new Promise((resolve) => setTimeout(resolve, RECEIPT_POLL_INTERVAL));
			await putRecieptInListReceipts();

			if (userReceiptReults.value.some((receipt) => !knownIds.has(receipt.id))) {
				break;
			}
		}

		await userInfo();
	};

	const userReceipts = async () => {
		if (loading.value || allLoaded.value) return;
		loading.value = true;
		try {
			setAuthHeader();
			const { data } = await axios.get(`${API_URL}/api/quiz/user/receipts?offset=${offsetReceipt.value}&limit=${limitReceipt}`);
			const results = data?.results || [];

			if (results.length < limitReceipt) {
				allLoaded.value = true;
			}
			// if(isMobile.value) {
			// 	allLoaded.value = true;
			// 	nextList.value = data.next;
			// }

			userReceiptReults.value = mergeReceipts(userReceiptReults.value, results);
			offsetReceipt.value += limitReceipt;
		} catch (error) {
			console.log("userReceipts:", error);
		} finally {
			loading.value = false;
		}
	};

	const showMoreReceipts = async () => {
		offsetReceipt.value += limitReceipt;
		await userReceipts();
	};

	const offsetPrize = ref(0);
	const limitPrize = 2;
	const loadingPrize = ref(false);
	const allLoadedPrize = ref(false);

	const updateWinnings = () => {
		winnersList.value = [];
		offsetPrize.value = 0;
		allLoadedPrize.value = false;
	};

	const getWinnings = async (type=null) => {
		if (loadingPrize.value || allLoadedPrize.value) return;
		loadingPrize.value = true;

		try {
			const { data } = await axios.get(`${API_URL}/api/quiz/winnings?offset=${offsetPrize.value}&limit=${limitPrize}&prize_type=${type}`);
			const results = data?.results || [];

			if (type === "grand") {
				console.log('res:',results);
				return results;
			}

			if (results.length < limitPrize) {
				allLoadedPrize.value = true;
			}

			winnersList.value.push(...results);
			offsetPrize.value += limitPrize;
		} catch (error) {
			console.log("getWinnings error:", error);
		} finally {
			loadingPrize.value = false;
		}
	};
	
	const getEmailWinnings = async (mail, type) => {
		loadingPrize.value = true;
		winnersList.value = [];
		allLoadedPrize.value = true;
		try {
			let allResults = [];
			const params = {
				email: mail,
			};
			if (type) params.prize_type = type;
			let url = `${API_URL}/api/quiz/winnings`;
			while (url) {
				const { data } = await axios.get(url, { params });
				url = data.next;
				allResults = [...allResults, ...data.results];
			}

			winnersList.value = allResults;
		} catch (error) {
			console.log("getEmailWinnings error:", error);
		} finally {
			loadingPrize.value = false;
		}
	};

	const offsetPrizeChange = () => {
		offsetPrize.value = 0;
	};

	const feedback = async () => {
		loaderBtn.value = true;

		const parameters = {
			topic: storeField.fields.select.id,
			name: storeField.fields.firstName.title || userData.value.first_name,
			email: storeField.fields.email.title || userData.value.email,
			files: storeField.fields.filesForPost,
			text: storeField.fields.message.title,
			is_personal_data_consent: storeField.fields.policy.title,
			agree_to_advertising: storeField.fields.agreement.title,
		};

		const formData = new FormData();

		if (parameters.files && parameters.files.length > 0) {
			for (const file of parameters.files) {
				formData.append("files", file);
			}
		}

		formData.append("topic", parameters.topic);
		formData.append("name", parameters.name);
		formData.append("email", parameters.email);
		formData.append("text", parameters.text);
		formData.append("is_personal_data_consent", parameters.is_personal_data_consent);
		formData.append("agree_to_advertising", parameters.agree_to_advertising);

		try {
			const res = await axios.post(`${API_URL}/api/feedback/send`, formData, {
				headers: {
					"Content-Type": "multipart/form-data",
				},
			});

			openModal("statusOk", { size: "w726" });
			responseError.value = "";
			storeField.updateFeedbackBtn();
			if (!isAuthenticated.value) {
				userData.value.first_name = "";
				userData.value.email = "";
			}
			sendMetrik("reachGoal", "Feedback_SentForm_Success");
		} catch (error) {
			responseError.value = error.response?.data?.detail || "Ошибка при отправке данных.";
			console.error("Error sendFeedback", error);
		} finally {
			loaderBtn.value = false;
		}
	};

	const topics = async () => {
		try {
			const res = await axios.get(`${API_URL}/api/feedback/topics?limit=1000`);
			return res.data.results;
		} catch (error) {
			console.error("Error sendFeedback", error);
		}
	};

	const logout = async () => {
		removeToken();
		delete axios.defaults.headers.common["Authorization"];
		clearGameData();
		storeQuizMatches.restartGame();
		userData.value = {};
		userReceiptsData.value = {};
		userReceiptReults.value = [];
		offsetReceipt.value = 0;
		allLoaded.value = false;
		listUserPrizes.value = [];
		storeField.updateFiles();
		storeField.updateBtnsTitles(['fio']);
		const widget = document.getElementById("apm-widget");
		if (widget) {
			widget.innerHTML = "";
		}
		router.push({ name: "home" });
	};

	const initWidget = () => {
		if (!userData.value || !userData.value.uuid) {
			console.warn("userData.value.uuid is not available");
			return;
		}

		if (!document.getElementById("apm-scan-qr")) {
			qrWidget.init("apm-widget", {
				api: "https://api.apmcheck.ru",
				apiKey: "cf4230f1-2278-455e-83e8-fbd4e354ad8d",
				userUuid: userData.value.uuid,
				callbacks: {
					onReceiptSentSuccess: () => {
						console.log("true:");
						refreshAfterReceiptSent();
						sendMetrik("reachGoal", "check_success");
						pushEvent("receipt-upload-success")
						smartPixel('download_receipt_success');
						trackAdriver('receipt-sent')
						// sendAdriver('check_download_success')
					},
					onReceiptSentError: () => {
						console.log("false:");
						// sendMetrik("reachGoal", "reciept_canceled");
					},
				},
				i18n: {
					screens: {
						qrParsed: {
							header: "Чек отправлен",
							subheader: "Ваш чек принят в обработку",
						},
						scanQr: {
							header: "Загрузка чека",
							subheader: "Наведите камеру на QR-код",
						},
						cameraNotAvailable: {
							subheader: "Мы не можем получить доступ к камере устройства.<br>Разреши браузеру обращаться к камере или введи данные с чека вручную",
						},
					},
				},
			});
		}
	};

	return {
		setAuthHeader,
		registration,
		authorization,
		recoveryPassword,
		logout,
		userInfo,
		verification,
		repeatVerification,
		userReceipts,
		showMoreReceipts,
		userPrizes,
		changeUserProfile,
		changeUserPassword,
		topics,
		feedback,
		updateWinnings,
		getWinnings,
		getEmailWinnings,
		offsetPrizeChange,
		getPrizes,
		getPoints,
		activatePromoCode,
		activatePendingPromoCodeIfExists,
		buyPrize,
		downloadCertificate,
		setToken,

		isAuthenticated,
		loaderBtn,
		userData,
		userReceiptReults,
		userReceiptsData,
		userPrizesData,
		listUserPrizes,
		responseError,
		allLoaded,
		loading,
		nextList,
		allLoadedPrize,
		loadingPrize,
		winnersList,
		isApprovedChecksCount,
		isRegistrationSuccess,
		isMatchGamePrize,
		listAllPrizes,
		pointsList,
		strUserLastFirsttName,
	};
});
